import React, { useEffect, useState } from "react"
import AldooStoreAPI from "aldoo-ra/Store/api"
import AddPaymentMethodDialog from "@components/Store/elements/add-payment-method"
import {
  CreditCardIcon,
  BuildingLibraryIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import LoadingOverlay from "aldoo-ra/LoadingOverlay"
import Button from "aldoo-ra/Button"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import useLogout from "aldoo-ra/PlatformAuth/actions/logout"

const PaymentMethodIcon = ({ method, className = "w-6 h-6 text-text_secondary" }) => {
  switch (method.type) {
    case "card":
      return <CreditCardIcon className={className} />
    case "us_bank_account":
    case "sepa_debit":
    case "bacs_debit":
    case "au_becs_debit":
      return <BuildingLibraryIcon className={className} />
    default:
      return <CreditCardIcon className={className} />
  }
}

const PaymentMethodCard = ({ method, defaultPaymentMethodId, onSetDefault, onDelete, texts }) => {
  const [loading, setLoading] = useState(false)
  const isDefault = method.id === defaultPaymentMethodId

  const handleSetDefault = async () => {
    if (loading || isDefault) return
    setLoading(true)
    try { await onSetDefault(method.id) } finally { setLoading(false) }
  }

  const handleDelete = async () => {
    if (loading || isDefault) return
    setLoading(true)
    try { await onDelete(method.id) } finally { setLoading(false) }
  }

  const getDisplayInfo = () => {
    if (method.type === "card") {
      return {
        title: `${method.card.brand.toUpperCase()} •••• ${method.card.last4}`,
        subtitle: `${texts?.expiration_text} ${method.card.exp_month}/${method.card.exp_year}`,
      }
    }
    if (method.type === "link") {
      return { title: "Link", subtitle: method.link?.email || "Payment method" }
    }
    return {
      title: method.type.replace("_", " ").toUpperCase(),
      subtitle: "Payment method",
    }
  }

  const { title, subtitle } = getDisplayInfo()

  return (
    <div className={`bg-paper rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
      isDefault ? "border-primary/40" : "border-bordered"
    }`}>
      <div className="flex items-center gap-3">
        <PaymentMethodIcon method={method} />
        <div>
          <p className="text-sm font-semibold text-text">{title}</p>
          <p className="text-xs text-text_secondary">{subtitle}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {isDefault ? (
          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
            <CheckIcon className="w-3.5 h-3.5" />
            {texts?.default_method}
          </span>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={handleSetDefault}
              disabled={loading}
              className="text-xs gap-1.5"
            >
              {loading
                ? <div className="w-3.5 h-3.5 border-b-2 border-primary rounded-full animate-spin" />
                : <ArrowPathIcon className="w-3.5 h-3.5" />
              }
              {texts?.cta_set_default}
            </Button>
            <Button
              variant="secondary_red"
              onClick={handleDelete}
              disabled={loading}
              className="text-xs gap-1.5"
            >
              {loading
                ? <div className="w-3.5 h-3.5 border-b-2 border-error rounded-full animate-spin" />
                : <XMarkIcon className="w-3.5 h-3.5" />
              }
              {texts?.cta_remove}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default function UserPaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState([])
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddPayment, setShowAddPayment] = useState(false)
  const [overlayLoading, setOverlayLoading] = useState(false)
  const logout = useLogout()
  const texts = TextBundle({ id: "payment_methods" })

  const fetchPaymentMethods = async () => {
    try {
      const response = await AldooStoreAPI.getPaymentMethods()
      if (response?.error) throw new Error(response.error)
      const methods = Array.isArray(response) ? response : []
      setPaymentMethods(methods)
      const defaultMethod = methods.find((m) => m.isDefault)
      setDefaultPaymentMethodId(defaultMethod?.id || null)
    } catch (err) {
      if (err.message?.includes("401") || err.message?.includes("403") ||
          err.message?.includes("Unauthorized") || err.message?.includes("Forbidden")) {
        setPaymentMethods([])
        setDefaultPaymentMethodId(null)
        try { await logout() } catch {}
      } else {
        setError(err.message)
        setPaymentMethods([])
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPaymentMethods()
    const handleUpdate = () => fetchPaymentMethods()
    window.addEventListener("payment-methods-updated", handleUpdate)
    return () => window.removeEventListener("payment-methods-updated", handleUpdate)
  }, [])

  const handleSetDefault = async (payment_method_id) => {
    setOverlayLoading(true)
    try {
      await AldooStoreAPI.attachPaymentMethod({ payment_method_id, makeDefault: true })
      await fetchPaymentMethods()
    } catch (err) {
      console.error(texts?.default_error, err)
    } finally {
      setOverlayLoading(false)
    }
  }

  const handleDelete = async (paymentMethodId) => {
    try {
      showPromptDialog({
        title: texts?.delete_dialog_title,
        message: texts?.delete_dialog_message,
        confirmText: texts?.cta_delete_confirm,
        onConfirm: async () => {
          setOverlayLoading(true)
          await AldooStoreAPI.detachPaymentMethod(paymentMethodId)
          await fetchPaymentMethods()
          setOverlayLoading(false)
        },
      })
    } catch (err) {
      console.error(texts?.delete_payment_error, err)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 rounded-2xl bg-error/10 text-error flex items-center gap-2">
        <ExclamationCircleIcon className="w-5 h-5 shrink-0" />
        <span className="text-sm">{texts?.error} {error}</span>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {paymentMethods.length > 0 && (
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => setShowAddPayment(true)} className="gap-2">
            <PlusIcon className="w-4 h-4" />
            {texts?.cta_add}
          </Button>
        </div>
      )}

      {paymentMethods.length === 0 ? (
        <div className="flex flex-col items-center text-center py-12 px-4 rounded-2xl border-2 border-dashed border-bordered gap-4">
          <CreditCardIcon className="w-10 h-10 text-text_secondary/40" />
          <p className="text-sm text-text_secondary">
            {texts?.no_payment_method}
          </p>
          <Button variant="secondary" onClick={() => setShowAddPayment(true)} className="gap-2">
            <PlusIcon className="w-4 h-4" />
            {texts?.cta_add}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {paymentMethods.map((method) => (
            <PaymentMethodCard
              key={method.id}
              method={method}
              defaultPaymentMethodId={defaultPaymentMethodId}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              texts={texts}
            />
          ))}
        </div>
      )}

      <LoadingOverlay
        title={texts?.loading_title}
        message={texts?.loading_message}
        loading={overlayLoading}
      />

      <AddPaymentMethodDialog
        isOpen={showAddPayment}
        paymentMethods={paymentMethods}
        onClose={() => setShowAddPayment(false)}
      />
    </div>
  )
}
